import axios from 'axios'
import moment from 'moment'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'

const RTOFollowup = forwardRef(({ data }, ref) => {
  const [alert, setAlert] = useState(null)
  const [followupList, setFollowupList] = useState([])
  const [followupDate, setFollowupDate] = useState(new Date())
  const [remarks, setRemarks] = useState('')
  const [details, setDetails] = useState([])

  const rtoRegisterId = data?.RTORegisterId
  const userId = useMemo(() => getStorageValues('userId'), [])

  useImperativeHandle(ref, () => ({
    refreshFollowup: fetchFollowups
  }))

  async function fetchDetails() {
    if (!rtoRegisterId) {
      setDetails([])
      return
    }
    try {
      const res = await axios.get(`${getApiBaseUrl()}/rtoregister/getbyid`, {
        params: { RTORegisterId: rtoRegisterId }
      })
      if (res.status === 200) {
        const payload = res?.data?.data
        const detailRows = payload?.details || []
        setDetails(detailRows)
      }
    } catch (err) {
      console.log(err)
    }
  }

  async function fetchFollowups() {
    if (!rtoRegisterId) {
      setFollowupList([])
      return
    }
    try {
      const params = { UserId: userId, RTORegisterId: rtoRegisterId }
      const res = await axios.get(`${getApiBaseUrl()}/rtoregister/getfollowupbyid`, { params })
      if (res.status === 200) {
        const payload = res?.data?.data
        const normalized = Array.isArray(payload) ? payload : Array.isArray(payload?.[0]) ? payload[0] : []
        setFollowupList(normalized)
      }
    } catch (err) {
      console.log(err)
      CreateAlert('Problem loading follow-up', 'Error', setAlert)
    }
  }

  async function saveFollowup() {
    if (!rtoRegisterId) {
      CreateAlert('Missing RTO register reference', 'Error', setAlert)
      return
    }
    if (!remarks.trim()) {
      CreateAlert('Please add remarks', 'Warning', setAlert)
      return
    }

    try {
      const payload = {
        UserId: userId,
        RTORegisterId: rtoRegisterId,
        FollowupDate: moment(followupDate).format('YYYY-MM-DD HH:mm:ss'),
        FollowupRemarks: remarks
      }

      const res = await axios.post(`${getApiBaseUrl()}/rtoregister/savefollowup`, payload)

      if (res.status === 200) {
        setRemarks('')
        fetchFollowups()
        CreateAlert('Follow-up saved!', 'Success', setAlert)
      }
    } catch (err) {
      console.log(err)
      CreateAlert('Problem saving follow-up', 'Error', setAlert)
    }
  }

  useEffect(() => {
    fetchDetails()
    fetchFollowups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtoRegisterId])

  return (
    <>
      <SKAlerts alert={alert} />

      <div className="row" style={{ margin: '10px' }}>
        <div className="col-12 mb-3">
          <div className="border rounded p-3">
            <h6 className="mb-2">Vehicle Details</h6>
            {details?.length ? (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Vehicle</th>
                      <th>Payment</th>
                      <th>Chassis</th>
                      <th>Engine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((d) => (
                      <tr key={d?.SalesRegisterDetailId}>
                        <td>{d?.VehicleLabel || '-'}</td>
                        <td>{d?.PaymentType || '-'}</td>
                        <td>{d?.ChassisNo || '-'}</td>
                        <td>{d?.EngineNo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted small">No vehicle details available.</div>
            )}
          </div>
        </div>

        <div className="col-lg-3">
          <label className="form-label">Next Follow-up Date</label>
          <DatePicker
            className="form-control"
            selected={followupDate}
            onChange={(date) => setFollowupDate(date)}
            minDate={new Date()}
          />
        </div>

        <div className="col-lg-9">
          <label className="form-label">Remarks</label>
          <textarea
            className="form-control"
            rows="1"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add follow-up details..."
          ></textarea>
          <div className="mt-2">
            <button className="btn btn-primary btn-sm me-2" onClick={saveFollowup}>
              Save
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setRemarks('')}>
              Cancel
            </button>
          </div>
        </div>

        <div className="col-12">
          <div className="scrollable-comments">
            {followupList?.map((f, i) => (
              <div className="card card-bg-light mg-em" key={f?.RTORegisterFollowupId || i}>
                <div className="card-body" style={{ margin: '5px' }}>
                  <div className="d-flex">
                    <div className="me-3">
                      <span className="avatar avatar-rounded">
                        <img
                          alt=""
                          src={`/assets/img/profiles/${f?.UserImagePath || 'avatar-03.jpg'}`}
                        />
                      </span>
                    </div>
                    <div>
                      <div className="fw-semibold">{f?.FullName || '-'}</div>
                      <div dangerouslySetInnerHTML={{ __html: f?.FollowupRemarks || '' }}></div>
                      <small className="text-muted">
                        {f?.CreatedDate ? moment(f.CreatedDate).format('MM/DD/YYYY, h:mm A') : ''}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {(!followupList || followupList.length === 0) && (
              <div className="text-muted small">No follow-ups available.</div>
            )}
          </div>
        </div>
      </div>
    </>
  )
})

export default RTOFollowup